import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import ExpenseFormModal from '../components/ExpenseFormModal';

const PAGE_SIZE = 10;

const ExpensesTab = () => {
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [summary, setSummary] = useState({
    this_month_total: 0,
    this_month_count: 0,
    today_total: 0,
    today_count: 0,
    top_type: null,
    by_type: [],
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [typeFilter, setTypeFilter] = useState('all'); // type id or 'all'
  const [modeFilter, setModeFilter] = useState('all'); // payment mode or 'all'
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, modeFilter, debouncedSearch, fromDate, toDate]);

  // Load expense types once for filter dropdown
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await chemicalService.getExpenseTypes({ page: 1, limit: 500 });
        setExpenseTypes(res.data || []);
      } catch (err) {
        console.error('Error loading expense types:', err);
      }
    };
    loadTypes();
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (typeFilter !== 'all') params.expense_type_id = typeFilter;
      if (modeFilter !== 'all') params.payment_mode = modeFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getExpenses(params),
        chemicalService.getExpensesSummary().catch((e) => {
          console.warn('Expenses summary not available:', e.message);
          return { data: { this_month_total: 0, this_month_count: 0, today_total: 0, today_count: 0, top_type: null, by_type: [] } };
        }),
      ]);

      setExpenses(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { this_month_total: 0, this_month_count: 0, today_total: 0, today_count: 0, top_type: null, by_type: [] });
    } catch (err) {
      console.error('Error fetching expenses:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, modeFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleAddClick = () => {
    setEditingExpense(null);
    setShowFormModal(true);
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setShowFormModal(true);
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(
      `Delete expense of Rs ${formatNumber(expense.amount)} (${expense.type_name})?\n\n` +
      `This cannot be undone.`
    )) return;

    try {
      await chemicalService.deleteExpense(expense.id);
      alert('✅ Expense deleted.');
      if (expenses.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchExpenses();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingExpense(null);
    fetchExpenses();
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>This Month's Expenses</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
            {formatLakh(summary.this_month_total)}
          </div>
          <div style={styles.summarySub}>{summary.this_month_count} entries</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f39c12' }}>
          <div style={styles.summaryLabel}>Today's Expenses</div>
          <div style={{ ...styles.summaryValue, color: '#f39c12' }}>
            Rs {formatNumber(summary.today_total)}
          </div>
          <div style={styles.summarySub}>{summary.today_count} entries</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3498db' }}>
          <div style={styles.summaryLabel}>Top Category</div>
          {summary.top_type ? (
            <>
              <div style={{ ...styles.summaryValue, color: summary.top_type.color || '#3498db', fontSize: '18px' }}>
                {summary.top_type.name}
              </div>
              <div style={styles.summarySub}>Rs {formatNumber(summary.top_type.total)} this month</div>
            </>
          ) : (
            <>
              <div style={{ ...styles.summaryValue, color: '#95a5a6', fontSize: '18px' }}>—</div>
              <div style={styles.summarySub}>No expenses yet</div>
            </>
          )}
        </div>
      </div>

      {/* ─── Filters Row ─── */}
      <div style={styles.toolbar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Types</option>
            {expenseTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Mode:</label>
          <select
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Modes</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="easypaisa">EasyPaisa</option>
            <option value="jazzcash">JazzCash</option>
          </select>
        </div>

        <div style={styles.dateRangeWrap}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={styles.dateInput}
            placeholder="From"
          />
          <span style={{ color: '#95a5a6', fontSize: '13px' }}>→</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={styles.dateInput}
            placeholder="To"
          />
          {(fromDate || toDate) && (
            <button onClick={clearDateFilters} style={styles.clearButton}>✕</button>
          )}
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search description, paid to..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={handleAddClick} style={styles.addButton}>
            + New Expense
          </button>
        </div>
      </div>

      {/* ─── Expenses Table ─── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Paid To</th>
              <th style={styles.th}>Mode</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading expenses...
                </td>
              </tr>
            ) : expenses.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>💸</div>
                  {debouncedSearch || typeFilter !== 'all' || modeFilter !== 'all' || fromDate || toDate
                    ? 'No expenses match your filters'
                    : expenseTypes.length === 0
                      ? 'Add expense types in Settings tab first, then record expenses here.'
                      : 'No expenses yet — click "+ New Expense" to record one'}
                </td>
              </tr>
            ) : (
              expenses.map((exp) => (
                <tr key={exp.id} style={styles.row}>
                  <td style={styles.td}>{formatDate(exp.expense_date)}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.typeBadge,
                      background: (exp.type_color || '#7f8c8d') + '20',
                      color: exp.type_color || '#7f8c8d',
                      border: `1px solid ${(exp.type_color || '#7f8c8d')}40`,
                    }}>
                      ● {exp.type_name}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {exp.description || <span style={{ color: '#bdc3c7', fontStyle: 'italic' }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    {exp.paid_to || <span style={{ color: '#bdc3c7', fontStyle: 'italic' }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.modeBadge}>{formatPaymentMode(exp.payment_mode)}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <strong style={{ color: '#e74c3c', fontSize: '15px' }}>
                      − Rs {formatNumber(exp.amount)}
                    </strong>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button onClick={() => handleEditClick(exp)} style={styles.editButton} title="Edit">
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(exp)} style={styles.deleteButton} title="Delete">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ─── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} expenses
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Modal ─── */}
      {showFormModal && (
        <ExpenseFormModal
          expense={editingExpense}
          onClose={() => { setShowFormModal(false); setEditingExpense(null); }}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 1000000) return `Rs ${(num / 1000000).toFixed(2)}M`;
  return `Rs ${formatNumber(num)}`;
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    bank_transfer: 'Bank',
    cheque: 'Cheque',
    easypaisa: 'EasyPaisa',
    jazzcash: 'JazzCash',
  };
  return map[m] || m;
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px', fontWeight: '600' },
  summaryValue: { fontSize: '28px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },

  toolbar: {
    background: 'white',
    padding: '14px 18px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '12px',
    alignItems: 'center',
    marginBottom: '16px',
  },
  filterGroup: {
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  filterLabel: {
    fontSize: '12px', fontWeight: '700', color: '#7f8c8d',
    textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  filterSelect: {
    padding: '8px 10px',
    border: '2px solid #e0e0e0', borderRadius: '7px',
    fontSize: '13px', fontFamily: 'inherit',
    background: 'white',
    cursor: 'pointer',
  },

  dateRangeWrap: {
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  dateInput: {
    padding: '7px 10px',
    border: '2px solid #e0e0e0', borderRadius: '7px',
    fontSize: '12px', fontFamily: 'inherit',
  },
  clearButton: {
    padding: '4px 10px',
    background: '#f0f0f0', color: '#7f8c8d',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },

  toolbarRight: {
    display: 'flex', gap: '10px', alignItems: 'center',
    flexWrap: 'wrap', marginLeft: 'auto',
  },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '240px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 18px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '700',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  typeBadge: {
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  modeBadge: {
    background: '#f0f0f0', color: '#555',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
  },

  editButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default ExpensesTab;